// Account page: manage your own bids — REFRESH a collection offer (re-funds it
// under a fresh throwaway key, nothing persisted: the wallet signs a new
// funding psbt and every item is re-signed locally, so it works from any
// browser) and CANCEL an item/collection bid (BIP-322 maker-signed). An inline
// script in account.html reveals the buttons only to the profile's owner.
import { ensureConnected } from "./connect.js";
import { flows } from "./trade.js";
import { toast } from "./ui.js";
import { pendingChanges, recordChange, type PlacedBid } from "./local-changes.js";
import { btcShort } from "./collection-bid.js";
import { t } from "../client/i18n.js";

let busy = false;

const run = (btn: HTMLButtonElement, fn: () => Promise<void>) => {
  if (busy) return;
  busy = true;
  const label = btn.textContent;
  btn.disabled = true;
  const reset = () => {
    btn.textContent = label;
    btn.disabled = false;
    busy = false;
  };
  // Success updates the DOM in place inside `fn`; either way, re-enable.
  void fn()
    .catch((err: unknown) => {
      toast(String((err as Error)?.message ?? err), "error");
    })
    .finally(reset);
};

document.addEventListener("click", (e) => {
  const refreshBtn = (e.target as Element).closest<HTMLButtonElement>(
    "[data-refresh-offer]",
  );
  if (refreshBtn?.dataset.refreshOffer) {
    run(refreshBtn, async () => {
      const account = await ensureConnected();
      if (!account) throw new Error(t("client.account.connect_first"));
      const id = refreshBtn.dataset.refreshOffer!;
      const refreshed = await flows.refreshCollectionOffer(account, id, (p) => {
        refreshBtn.textContent = p.toUpperCase();
      });
      toast(t("client.account.offer_refreshed", { n: refreshed }), "ok");
      applyCollRefreshed(id);
      recordChange({ t: "coll-refreshed", id });
    });
    return;
  }

  const cancelBtn = (e.target as Element).closest<HTMLButtonElement>(
    "[data-cancel-order]",
  );
  if (cancelBtn?.dataset.cancelOrder) {
    run(cancelBtn, async () => {
      // The reveal script only shows these when the connected wallet IS this
      // profile's address — the maker who must sign the cancel message.
      // A bid cancels whole: every component row of it goes.
      const maker = document.getElementById("account-head")!.dataset.address!;
      const bidId = cancelBtn.dataset.cancelOrder!;
      await flows.cancelBids(maker, { bidIds: [bidId] });
      toast(t("client.account.bid_cancelled"), "ok");
      removeBidRows(bidId);
      recordChange({ t: "bid-cancelled", bidId });
    });
  }
});

/** Every component row of a bid. */
function removeBidRows(bidId: string): void {
  for (const tr of document.querySelectorAll(`tr[data-coll-row][data-bid="${bidId}"]`)) tr.remove();
}

/** One fill of a bid was taken: its rows have one fewer left (or go). */
function consumeFill(bidId: string): void {
  for (const tr of document.querySelectorAll<HTMLElement>(`tr[data-coll-row][data-bid="${bidId}"]`)) {
    const tag = tr.querySelector<HTMLElement>("td.num .dim");
    const left = Number(/×(\d+)/.exec(tag?.textContent ?? "")?.[1] ?? 1) - 1;
    if (left <= 0) tr.remove();
    else if (tag) tag.textContent = left > 1 ? t("client.account.left", { n: left }) : "";
  }
}

/** Rewrite an offer's acceptable counts to total/total and drop its REFRESH
 * button — idempotent (no-op when already fresh or absent). */
function applyCollRefreshed(id: string): void {
  const counts = document.querySelector<HTMLElement>(`[data-coll-counts="${id}"]`);
  if (counts?.dataset.total) {
    counts.textContent = t("client.account.acceptable", { valid: counts.dataset.total ?? "", total: counts.dataset.total ?? "" });
  }
  document.querySelector(`[data-refresh-offer="${id}"]`)?.remove();
}

// ---- local-changes appliers (all idempotent) ---------------------------------

const profileAddress = document.getElementById("account-head")?.dataset.address;
const spriteCol = (id: number) => (id - 1) % 100;
const spriteRow = (id: number) => Math.floor((id - 1) / 100);

/** The monke cell: sprite tile (recreated locally from the 10k sheet — it is
 * only a CSS background keyed by --col/--row) + numbered link, or a dash. */
function monkeCell(td: HTMLTableCellElement, monkeId: number | null): void {
  if (monkeId == null) {
    td.textContent = "—";
    return;
  }
  const a = document.createElement("a");
  a.href = `/${monkeId}`;
  const sprite = document.createElement("span");
  sprite.className = "msprite s28";
  sprite.style.setProperty("--col", String(spriteCol(monkeId)));
  sprite.style.setProperty("--row", String(spriteRow(monkeId)));
  sprite.setAttribute("role", "img");
  sprite.setAttribute("aria-label", `#${monkeId}`);
  a.append(sprite, document.createTextNode(` #${monkeId}`));
  td.appendChild(a);
}

function priceCell(td: HTMLTableCellElement, priceSats: number): void {
  td.className = "num";
  const span = document.createElement("span");
  span.className = "price-btc";
  span.textContent = btcShort(priceSats);
  td.appendChild(span);
}

function cancelButton(id: string, kind: "offer" | "collection offer"): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "btn btn-ghost";
  b.dataset.cancelOrder = id;
  b.dataset.kind = kind;
  b.textContent = t("client.account.cancel");
  return b; // own profile — visible (the reveal script gated SSR buttons the same way)
}


function insertCollRows(bid: PlacedBid): void {
  const rows = document.getElementById("offers-made-rows");
  if (!rows) return;
  for (const c of bid.components) {
    const key = `${bid.id}:${c.componentIndex}`;
    if (document.querySelector(`tr[data-coll-row="${key}"]`)) continue;
    const monkes = (c.attributeFilter as { monkes?: number[] } | null)?.monkes;
    const scope = Array.isArray(monkes)
      ? monkes.map((n) => `#${n}`).join(", ")
      : c.attributeFilter && Object.keys(c.attributeFilter).length
        ? Object.entries(c.attributeFilter)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")
        : t("client.account.any_monke");
    const tr = document.createElement("tr");
    tr.dataset.collRow = key;
    tr.dataset.bid = bid.id;
    tr.dataset.offer = c.offerId;
    tr.insertCell().textContent = "—";
    const price = tr.insertCell();
    priceCell(price, c.priceSats);
    const left = document.createElement("span");
    left.className = "dim";
    left.textContent = c.left > 1 ? t("client.account.left_spaced", { n: c.left }) : "";
    price.appendChild(left);
    const note = tr.insertCell();
    note.className = "dim";
    const counts = document.createElement("span");
    counts.dataset.collCounts = c.offerId;
    counts.dataset.total = String(c.totalItems);
    counts.textContent = t("client.account.acceptable", { valid: c.totalItems, total: c.totalItems });
    note.append(
      document.createTextNode(t("client.account.bid_scope", { scope })),
      counts,
      document.createTextNode(" "),
      cancelButton(bid.id, "collection offer"),
    );
    rows.appendChild(tr);
  }
  document.getElementById("offers-made-panel")?.removeAttribute("hidden");
}

function insertListingRow(listing: {
  id: string;
  kind: "public" | "private";
  monkeId: number;
  priceSats: number;
}): void {
  const rows = document.getElementById("listings-rows");
  if (!rows || document.querySelector(`tr[data-listing-row="${listing.id}"]`)) return;
  const tr = document.createElement("tr");
  tr.dataset.listingRow = listing.id;
  monkeCell(tr.insertCell(), listing.monkeId);
  const variant = tr.insertCell();
  variant.className = "dim";
  variant.textContent = listing.kind === "public" ? t("client.account.listing_public") : t("client.account.listing_private");
  priceCell(tr.insertCell(), listing.priceSats);
  rows.prepend(tr);
  document.getElementById("listings-panel")?.removeAttribute("hidden");
}

// Layer recent local changes over this (possibly SWR-cached) render — inserts
// only apply on the actor's own profile (local changes are this wallet's).
for (const c of pendingChanges()) {
  if (c.t === "coll-cancelled") {
    document.querySelector(`tr[data-coll-row][data-offer="${c.id}"]`)?.remove();
  } else if (c.t === "bid-cancelled") {
    removeBidRows(c.bidId);
  } else if (c.t === "component-cancelled") {
    document.querySelector(`tr[data-coll-row="${c.bidId}:${c.componentIndex}"]`)?.remove();
  } else if (c.t === "slot-filled") {
    if (document.querySelector(`tr[data-coll-row][data-offer="${c.offerId}"]`)) consumeFill(c.bidId);
  } else if (c.t === "coll-refreshed") {
    applyCollRefreshed(c.id);
  } else if (c.t === "bid-placed" && c.bid.bidder === profileAddress) {
    insertCollRows(c.bid);
  } else if (c.t === "listing-created" && c.listing.lister === profileAddress) {
    insertListingRow(c.listing);
  } else if (c.t === "listing-cancelled") {
    document.querySelector(`tr[data-listing-row="${c.id}"]`)?.remove();
  }
}
